import React, { useState } from 'react';
import { bool, string } from 'prop-types';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage } from '../../util/reactIntl';
import { pathByRouteName } from '../../util/routes';
import { propTypes } from '../../util/types';
import { userDisplayNameAsString } from '../../util/data';
import { updateBlockedUser } from '../../ducks/user.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';

import { PrimaryButton, SecondaryButton } from '../Button/Button';
import Modal from '../Modal/Modal';

import css from './BlockUser.module.css';

/**
 * "Block user" / "Unblock user" (P25028-272), on the profile page and the transaction
 * page.
 *
 * A quiet text link, like "Report listing": it is a rare action and must not compete
 * with the order buttons. Blocking asks first, in a pop-up that also links to the report
 * flow (the Contact Us page, in a new tab so the pop-up stays). Unblocking happens at once.
 *
 * The block list is the signed-in user's own private data, so saving it updates the
 * current user in the store and every page reacts straight away.
 */
const BlockUserButton = props => {
  const { className, otherUser, blockedByMe } = props;
  const dispatch = useDispatch();
  const routeConfiguration = useRouteConfiguration();
  const [modalOpen, setModalOpen] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const otherUserId = otherUser?.id?.uuid;
  if (!otherUserId) {
    return null;
  }
  const name = userDisplayNameAsString(otherUser, '');
  const modalId = `BlockUserButton.modal.${otherUserId}`;

  const save = block => {
    setInProgress(true);
    setSaveFailed(false);
    return dispatch(updateBlockedUser({ userId: otherUserId, block }))
      .then(() => {
        setInProgress(false);
        setModalOpen(false);
      })
      .catch(() => {
        setInProgress(false);
        setSaveFailed(true);
      });
  };

  const openModal = () => {
    setSaveFailed(false);
    setModalOpen(true);
  };
  const closeModal = () => {
    if (!inProgress) {
      setModalOpen(false);
    }
  };

  return (
    <span className={classNames(css.root, className)}>
      {blockedByMe ? (
        <button
          type="button"
          className={css.textButton}
          onClick={() => save(false)}
          disabled={inProgress}
        >
          <FormattedMessage id="BlockUser.unblockUser" />
        </button>
      ) : (
        <button type="button" className={css.textButton} onClick={openModal}>
          <FormattedMessage id="BlockUser.blockUser" />
        </button>
      )}
      {saveFailed && !modalOpen ? (
        <span className={css.error}>
          <FormattedMessage id="BlockUser.saveFailed" />
        </span>
      ) : null}

      <Modal
        id={modalId}
        isOpen={modalOpen}
        onClose={closeModal}
        onManageDisableScrolling={(componentId, disable) =>
          dispatch(manageDisableScrolling(componentId, disable))
        }
        usePortal
      >
        <div className={css.modal}>
          <h2 className={css.modalTitle}>
            <FormattedMessage id="BlockUser.confirmTitle" values={{ name }} />
          </h2>
          <p className={css.modalText}>
            <FormattedMessage id="BlockUser.confirmText" />
          </p>
          {/* "Also": report as well as block. A new tab keeps this pop-up open, so the
              user can still press Block after reporting. NamedLink drops `target`. */}
          <a
            className={css.reportLink}
            href={pathByRouteName('CMSPage', routeConfiguration, { pageId: 'contact' })}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FormattedMessage id="BlockUser.alsoReport" />
          </a>
          {saveFailed ? (
            <p className={css.error}>
              <FormattedMessage id="BlockUser.saveFailed" />
            </p>
          ) : null}
          <div className={css.modalButtons}>
            <PrimaryButton
              type="button"
              className={css.blockButton}
              onClick={() => save(true)}
              inProgress={inProgress}
              disabled={inProgress}
            >
              <FormattedMessage id="BlockUser.confirmBlock" />
            </PrimaryButton>
            <SecondaryButton
              type="button"
              className={css.cancelButton}
              onClick={closeModal}
              disabled={inProgress}
            >
              <FormattedMessage id="BlockUser.cancel" />
            </SecondaryButton>
          </div>
        </div>
      </Modal>
    </span>
  );
};

BlockUserButton.defaultProps = {
  className: null,
  otherUser: null,
  blockedByMe: false,
};

BlockUserButton.propTypes = {
  className: string,
  otherUser: propTypes.user,
  blockedByMe: bool,
};

export default BlockUserButton;
